import { useEffect, useMemo } from 'react';
import { useTenantConfig } from './useTenantConfig';
import { hexToHsl } from '@/lib/color';

const CSS_VAR_MAP: Record<string, { varName: string; format: 'hsl' | 'hex' }[]> = {
  primary_color: [
    { varName: '--primary', format: 'hsl' },
    // Chat bubbles follow the tenant's primary color when whitelabeled;
    // the default (no override) stays iMessage blue via :root in index.css.
    { varName: '--chat-bubble-color', format: 'hex' },
  ],
  secondary_color: [{ varName: '--secondary', format: 'hsl' }],
  chart_1_color: [{ varName: '--chart-1', format: 'hex' }],
  chart_2_color: [{ varName: '--chart-2', format: 'hex' }],
  chart_3_color: [{ varName: '--chart-3', format: 'hex' }],
  chart_4_color: [{ varName: '--chart-4', format: 'hex' }],
  chart_5_color: [{ varName: '--chart-5', format: 'hex' }],
  card_header_bg_color: [{ varName: '--card-header-bg', format: 'hex' }],
  card_title_color: [{ varName: '--card-title-color', format: 'hex' }],
  page_bg_color: [{ varName: '--page-bg', format: 'hsl' }],
  sidebar_bg_color: [{ varName: '--sidebar', format: 'hsl' }],
  card_bg_color: [{ varName: '--card', format: 'hsl' }],
};

const FONT_FILE_RE = /\.(woff2?|ttf|otf|eot)(\?.*)?$/i;

const FONT_EXT_FORMAT: Record<string, string> = {
  woff2: 'woff2', woff: 'woff', ttf: 'truetype', otf: 'opentype', eot: 'embedded-opentype',
};

const FONT_FIELDS: { urlKey: string; familyKey: string; varName: string; linkId: string }[] = [
  { urlKey: 'ui_font_url', familyKey: 'ui_font_family', varName: '--font-sans', linkId: 'plb-ui-font' },
  { urlKey: 'metric_font_url', familyKey: 'metric_font_family', varName: '--font-metric', linkId: 'plb-metric-font' },
];

function applyWhitelabeling(wl: Record<string, any> | undefined) {
  const root = document.documentElement;

  for (const [field, vars] of Object.entries(CSS_VAR_MAP)) {
    const value = wl?.[field] as string | undefined;
    for (const { varName, format } of vars) {
      if (value) {
        root.style.setProperty(varName, format === 'hsl' ? hexToHsl(value) : value);
      } else {
        root.style.removeProperty(varName);
      }
    }
  }

  for (const { urlKey, familyKey, varName, linkId } of FONT_FIELDS) {
    const url = wl?.[urlKey] as string | undefined;
    const family = wl?.[familyKey] as string | undefined;
    const isFile = url ? FONT_FILE_RE.test(url) : false;
    const styleId = `${linkId}-style`;

    // Clean up previous elements
    const prevLink = document.getElementById(linkId);
    const prevStyle = document.getElementById(styleId);

    if (url && isFile && family) {
      // Uploaded font file — inject @font-face via <style>
      if (prevLink) prevLink.remove();
      const ext = url.match(/\.(\w+)(\?.*)?$/)?.[1]?.toLowerCase() || 'woff2';
      const format = FONT_EXT_FORMAT[ext] || 'woff2';
      const css = `@font-face { font-family: "${family}"; src: url("${url}") format("${format}"); font-display: swap; }`;
      if (prevStyle) {
        prevStyle.textContent = css;
      } else {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
      }
    } else if (url && !isFile) {
      // External stylesheet URL (e.g. Google Fonts)
      if (prevStyle) prevStyle.remove();
      let link = prevLink as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      link.href = url;
    } else {
      if (prevLink) prevLink.remove();
      if (prevStyle) prevStyle.remove();
    }

    if (family) {
      root.style.setProperty(varName, `"${family}", sans-serif`);
    } else {
      root.style.removeProperty(varName);
    }
  }

  document.title = wl?.page_title || 'Pliable';

  const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"][sizes="32x32"]');
  if (faviconLink) {
    faviconLink.href = wl?.favicon_path || '/favicon/favicon-32x32.png';
  }
}

// Apply whitelabeling from __PLB_ENV__ immediately (before React mounts)
// to prevent flicker on custom domains
if (typeof window !== 'undefined') {
  const envWhitelabeling = (window as any).__PLB_ENV__?.whitelabeling;
  if (envWhitelabeling) {
    applyWhitelabeling(envWhitelabeling);
  }
}

/**
 * Synchronous getter for a whitelabel value.
 * Checks __PLB_ENV__ (available immediately, no async) first.
 * Respects the enabled flag — returns undefined if whitelabeling is disabled.
 */
export function getWhitelabelValue(key: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const wl = (window as any).__PLB_ENV__?.whitelabeling;
  if (!wl || wl.enabled === false) return undefined;
  return wl[key] as string | undefined;
}

/**
 * React hook that resolves a whitelabel value.
 * Tries tenant config (async, most up-to-date) → __PLB_ENV__ (sync, bootstrap) → fallback.
 * Returns the fallback if whitelabeling is disabled.
 */
export function useWhitelabelValue(key: string, fallback: string): string {
  const { data: tenantConfig } = useTenantConfig();

  return useMemo(() => {
    const wl = tenantConfig?.whitelabeling;
    if (wl && wl.enabled !== false) {
      const val = (wl as Record<string, any>)[key] as string | undefined;
      if (val) return val;
    }
    return getWhitelabelValue(key) || fallback;
  }, [tenantConfig, key, fallback]);
}

/**
 * The tenant's whitelabel config, or undefined when whitelabeling is off.
 *
 * For callers that need several fields at once — the dashboard theme editor
 * resolves nine of them to show what a field inherits — rather than one hook
 * call per key.
 */
export function useWhitelabelConfig(): Record<string, any> | undefined {
  const { data: tenantConfig } = useTenantConfig();

  return useMemo(() => {
    const wl = tenantConfig?.whitelabeling as Record<string, any> | undefined;
    if (wl && wl.enabled !== false) return wl;
    if (wl && wl.enabled === false) return undefined;
    const env = (typeof window !== 'undefined'
      ? (window as any).__PLB_ENV__?.whitelabeling
      : undefined) as Record<string, any> | undefined;
    return env && env.enabled !== false ? env : undefined;
  }, [tenantConfig]);
}

/** Convenience hook for the platform display name. */
export function usePlatformName(): string {
  return useWhitelabelValue('page_title', 'Pliable');
}

/** Whether whitelabeling is enabled for this tenant. */
export function useHasCustomBranding(): boolean {
  const { data: tenantConfig } = useTenantConfig();
  const wl = tenantConfig?.whitelabeling;
  if (wl) return wl.enabled !== false;
  // Fall back to __PLB_ENV__ before tenant config loads
  const envWl = typeof window !== 'undefined' ? (window as any).__PLB_ENV__?.whitelabeling : undefined;
  return envWl?.enabled !== false && !!envWl;
}

export function useWhitelabelOverrides() {
  const { data: tenantConfig } = useTenantConfig();

  useEffect(() => {
    // Don't clear overrides while tenantConfig is still loading —
    // the module-level __PLB_ENV__ apply is already active.
    if (!tenantConfig) return;

    const wl = tenantConfig.whitelabeling;
    const isEnabled = wl?.enabled !== false;
    applyWhitelabeling(isEnabled ? wl : undefined);
  }, [tenantConfig]);
}
